import type { ReactNode } from 'react'
import { Check } from 'lucide-react'
import clsx from 'clsx'
import { POOL_TIERS, RDS_POOL_LABEL, memoryText, resolveVm, tierHeadline, vcpuText, type PoolTierId } from '@/data/sizeCatalog'
import styles from './PoolTierSelector.module.css'

interface PoolTierSelectorProps {
  /** The radio group's name. Must be unique among the selectors on a page. */
  name: string
  legend: string
  /** The selected tier. */
  value?: PoolTierId
  /** Without it the selector is a read-only comparison of the tiers, with nothing to choose. */
  onChange?: (tier: PoolTierId) => void
  hint?: ReactNode
}

/**
 * The four RDS pool tiers as cards. A tier is the ONLY size the user chooses: it sets how many sessions the RDSH pool takes, and every
 * tier is built on the same VM. Each card shows its name and its sessions first ("Premium · 100 sessions"), then the pool, vCPU, memory
 * and disk, all read from the size catalog. Interactive, it is a radio group made of real radio inputs, so arrow keys, Tab and screen
 * readers behave natively. Read-only, it is a plain comparison.
 *
 * "Dedicated Collection" is a pool tier. It is not the Dedicated or Existing choice of a server, and its card says so.
 */
export function PoolTierSelector({ name, legend, value, onChange, hint }: PoolTierSelectorProps) {
  const interactive = onChange !== undefined
  return (
    <fieldset className={styles.group}>
      <legend className={styles.legend}>{legend}</legend>
      <div className={styles.grid}>
        {POOL_TIERS.map((tier) => {
          const vm = resolveVm('rds', tier.id)
          const selected = interactive && value === tier.id
          const headId = `${name}-${tier.id}-head`
          const detailId = `${name}-${tier.id}-detail`
          const body = (
            <>
              <span className={styles.top}>
                <span id={headId} className={styles.head}>
                  <strong className={styles.sessions}>{tierHeadline(tier)}</strong>
                </span>
                {selected && <Check size={14} className={styles.check} aria-hidden="true" />}
              </span>
              <span className={styles.tierName}>{tier.label}</span>
              {tier.id === 'DED' && <span className={styles.tierNote}>A pool tier. Not the Dedicated or Existing choice of a server.</span>}
              <dl id={detailId} className={styles.details}>
                <div><dt>Pool</dt><dd>{RDS_POOL_LABEL}</dd></div>
                <div><dt>CPU</dt><dd>{vcpuText(vm)}</dd></div>
                <div><dt>Memory</dt><dd>{memoryText(vm)}</dd></div>
                <div><dt>Disk</dt><dd>{vm.disk}</dd></div>
                <div><dt>Azure VM SKU</dt><dd className={styles.sku}>{vm.sku}</dd></div>
              </dl>
            </>
          )
          return interactive ? (
            <label key={tier.id} className={clsx(styles.card, styles.interactive, selected && styles.selected)}>
              <input
                type="radio"
                className={styles.radio}
                name={name}
                value={tier.id}
                checked={selected}
                onChange={() => onChange(tier.id)}
                aria-labelledby={headId}
                aria-describedby={detailId}
              />
              {body}
            </label>
          ) : (
            <div key={tier.id} className={styles.card} role="group" aria-labelledby={headId} aria-describedby={detailId}>
              {body}
            </div>
          )
        })}
      </div>
      {hint && <p className={styles.hint}>{hint}</p>}
    </fieldset>
  )
}
